import type { HomebrewPackDefs } from '@dl/shared';

/**
 * Campaign-local homebrew Pack overlay (#73 / ADR-0001).
 *
 * The GM-authored predicate + trait definitions, folded from the
 * Campaign op log's `__pack` meta-ops by `foldPackOps`. The substrate
 * orchestration (`app.ts:rebuildEntities`) calls `setHomebrewDefs`
 * whenever the op log rebuilds; the three bundled-pack-derived
 * singletons (`loadGmRegistry`, `getTraitIndex` in fact-row-editor, and
 * `allTraitDeclarations` in trait-attach-view) overlay these defs on top
 * of `core` and invalidate their memo when `homebrewEpoch()` changes.
 *
 * A module-scope holder (not Model state) because it's read
 * synchronously inside reactive accessors and llui's Model must stay
 * serialisable. Re-render is driven by the `entities-rebuilt` dispatch
 * that fires from the same fold — the view re-reads the rebuilt
 * singletons on the next commit.
 */
/** A fresh, fully-populated empty overlay (all lanes present). */
function emptyDefs(): HomebrewPackDefs {
  return {
    predicates: [],
    traits: [],
    types: [],
    dimensions: [],
    units: [],
    calendars: [],
    retiredDimensions: {},
    retiredUnits: {},
    retiredCalendars: {},
    aliases: {},
    aliasAdvisories: [],
  };
}

let _defs: HomebrewPackDefs = emptyDefs();
let _epoch = 0;

/** Replace the homebrew overlay and bump the epoch (invalidates memos). */
export function setHomebrewDefs(defs: HomebrewPackDefs): void {
  _defs = defs;
  _epoch += 1;
}

/** The current homebrew overlay. */
export function getHomebrewDefs(): HomebrewPackDefs {
  return _defs;
}

/** Monotonic counter — bumps on every `setHomebrewDefs`. Memo cache key. */
export function homebrewEpoch(): number {
  return _epoch;
}

/** Test seam — reset the overlay to empty. */
export function _resetHomebrewDefs(): void {
  _defs = emptyDefs();
  _epoch += 1;
}
